import React, { useId } from 'react';
import { FmActionCardGrid, type FmActionCardItem } from '../FmActionCardGrid';
import { FmBanner } from '../FmBanner';
import { FmButton, type FmButtonVariant } from '../FmButton';
import { FmCapabilityShowcase, type FmCapabilityShowcaseItem } from '../FmCapabilityShowcase';
import { FmConfirm } from '../FmConfirm';
import { FmEvidence } from '../FmEvidence';
import { FmField } from '../FmField';
import { FmSelect } from '../FmSelect';
import { FmSelector } from '../FmSelector';
import type { FmStoryAction } from './elementFields.types';
import type { FmStoryActionSet, FmStoryActionSetItem } from './elementMedia.types';
import { derivedTestId, useElementContext } from './elementContext';
import { FmChartElementView, FmImageElementView, FmMapElementView } from './FmMediaElements';
import type {
  FmActionsElement,
  FmLensElement,
  FmNoticeElement,
  FmProvenanceElement,
} from './elements.types';
import './FmElements.css';

/**
 * One story action as a standard-size button; a confirmation gates its onClick
 * behind FmConfirm. A disabled action prints its reason under the button,
 * because a tooltip on a disabled control never shows on touch.
 */
export const FmActionButton: React.FC<{ action: FmStoryAction; variant: FmButtonVariant }> = ({
  action,
  variant,
}) => {
  const confirmation = 'confirmation' in action ? action.confirmation : undefined;
  const disabledReason =
    'disabled' in action && action.disabled === true ? action.disabledReason : undefined;
  const button = (
    <FmButton
      variant={variant}
      size="lg"
      href={confirmation ? undefined : action.href}
      onClick={confirmation ? undefined : action.onClick}
      disabled={disabledReason !== undefined}
      data-testid={action.testId}
    >
      {action.label}
    </FmButton>
  );
  if (disabledReason !== undefined) {
    return (
      <span className="fm-element-action">
        {button}
        <small
          className="fm-element-action__reason"
          data-testid={action.testId ? `${action.testId}-reason` : undefined}
        >
          {disabledReason}
        </small>
      </span>
    );
  }
  if (!confirmation || !action.onClick) return button;
  return (
    <FmConfirm
      title={confirmation.title}
      description={confirmation.description}
      affectedEntities={confirmation.affectedEntities}
      confirmText={confirmation.confirmLabel}
      cancelText={confirmation.cancelLabel}
      onConfirm={action.onClick}
      contentTestId={action.testId ? `${action.testId}-confirmation` : undefined}
    >
      {button}
    </FmConfirm>
  );
};

function itemDisabledReason(item: FmStoryActionSetItem): string | undefined {
  return item.disabledReason ?? ('disabled' in item.cta ? item.cta.disabledReason : undefined);
}

function itemMedia(item: FmStoryActionSetItem): React.ReactNode {
  const media = item.media;
  if (!media) return undefined;
  if (media.kind === 'chart') {
    const { kind: _kind, ...chart } = media;
    return <FmChartElementView element={{ element: 'chart', ...chart }} />;
  }
  if (media.kind === 'image') {
    const { kind: _kind, ...image } = media;
    return <FmImageElementView element={{ element: 'image', ...image }} cropRole="card" />;
  }
  const { kind, ...map } = media;
  return (
    <FmMapElementView
      element={{ element: 'map', ...map }}
      cropRole="card"
      defaultTestId={`story-${kind}-media`}
    />
  );
}

/** An action set: a switcher that shows each option's media, or a grid of option cards. */
const ActionSetView: React.FC<{ set: FmStoryActionSet; testId: string }> = ({ set, testId }) => {
  const { contrast } = useElementContext();
  if ((set.presentation ?? 'switcher') === 'switcher') {
    const items: FmCapabilityShowcaseItem[] = set.items.map((item) => ({
      key: item.key,
      label: item.label,
      description: item.description,
      tags: item.tags,
      statusLabel: item.statusLabel,
      disabledReason: itemDisabledReason(item),
      cta: { label: item.cta.label, href: item.cta.href, onClick: item.cta.onClick },
      media: itemMedia(item),
      'data-testid': item.testId,
    }));
    return (
      <FmCapabilityShowcase
        items={items}
        activeKey={set.activeKey}
        defaultActiveKey={set.defaultActiveKey}
        onActiveChange={set.onChange}
        autoCycleMs={set.autoCycleMs}
        side={set.mediaSide ?? 'left'}
        tone={contrast}
        data-testid={testId}
      />
    );
  }
  const cards: FmActionCardItem[] = set.items.map((item) => ({
    key: item.key,
    iconKey: item.iconKey,
    label: item.label,
    description: item.description,
    tags: item.tags,
    statusLabel: item.statusLabel,
    disabledReason: itemDisabledReason(item),
    cta: {
      label: item.cta.label,
      href: item.cta.href,
      onClick: item.cta.onClick,
      confirmation: 'confirmation' in item.cta ? item.cta.confirmation : undefined,
    },
    'data-testid': item.testId ?? item.cta.testId,
  }));
  return <FmActionCardGrid items={cards} columns={set.columns ?? 3} data-testid={testId} />;
};

/** The section's next steps: at most one primary, quiet secondaries, or an action set. */
export const FmActionsElementView: React.FC<{ element: FmActionsElement }> = ({ element }) => {
  const context = useElementContext();
  if (element.set) {
    return (
      <ActionSetView
        set={element.set}
        testId={derivedTestId(context, element.testId, 'action-set')}
      />
    );
  }
  return (
    <div className="fm-element-actions" data-testid={element.testId}>
      {element.primary ? <FmActionButton action={element.primary} variant="primary" /> : null}
      {element.secondary?.map((action) => (
        <FmActionButton key={action.label} action={action} variant="bordered" />
      ))}
    </div>
  );
};

/** A data-gated advisory: the descriptor exists only when the served data warrants it. */
export const FmNoticeElementView: React.FC<{ element: FmNoticeElement }> = ({ element }) => (
  <FmBanner
    tone={element.tone}
    title={element.title}
    description={element.description}
    data-testid={element.testId}
  />
);

/** Source, date and availability of what the section shows; ids stay `<base>-evidence`. */
export const FmProvenanceElementView: React.FC<{ element: FmProvenanceElement }> = ({
  element,
}) => {
  const context = useElementContext();
  return (
    <FmEvidence
      evidence={element.evidence}
      data-testid={derivedTestId(context, element.testId, 'evidence')}
    />
  );
};

/**
 * The lens switches the payload of its neighbouring element (series, unit or
 * scenario). The route owns the value and rebuilds that payload; the lens only
 * renders the choice, as one segmented or radio selector, or a select. Its test
 * ids hang off the control id (`<id>-chart-switcher`, `<id>-<option>-selector`),
 * which routes keep stable across section moves.
 */
export const FmLensElementView: React.FC<{ element: FmLensElement }> = ({ element }) => {
  const { control } = element;
  const labelId = useId();
  const testId = element.testId ?? control.testId ?? `${control.id}-chart-switcher`;
  const disabled = Boolean(control.disabledReason);
  const onChange = (value: string): void => control.onChange?.(value);
  return (
    <div className="fm-element-lens">
      {control.presentation === 'select' ? (
        <FmField label={control.label}>
          <FmSelect
            value={control.value ?? undefined}
            options={control.options.map((option) => ({ ...option }))}
            onChange={(value) => onChange(String(value))}
            disabled={disabled}
            ariaLabel={control.label}
            controlWidth="fill"
            data-testid={testId}
          />
        </FmField>
      ) : (
        <div className="fm-field">
          <span className="fm-field__label" id={labelId}>
            {control.label}
          </span>
          <div className="fm-field__control">
            <FmSelector
              variant={control.presentation}
              value={control.value}
              label={control.label}
              labelledBy={labelId}
              items={control.options.map((option) => ({
                value: option.value,
                label: option.label,
                disabled: disabled || option.disabled,
                'data-testid': `${control.id}-${option.value}-selector`,
              }))}
              onChange={onChange}
              data-testid={testId}
            />
          </div>
        </div>
      )}
      {control.disabledReason ? <small>{control.disabledReason}</small> : null}
    </div>
  );
};
